// Frontend for /app — single source of UI behavior. The wire
// contract is now plain HTTP (no SSE): /api/v1/names/generate for AI,
// /api/v1/availability/check (NDJSON progressive) for batched DNS,
// /api/v1/whois/lookup for registry detail.
(function () {
  function setupSession() {
    var input = document.getElementById('main-in');
    if (!input) return; // not the /app page; bail

    // ---------- Constants -------------------------------------------
    var REQUIRED_TLDS = ['.com', '.net'];
    // "Popular" TLDs surface automatically when the row expands;
    // "extended" TLDs only show after the user explicitly asks for
    // them (cheaper upstream footprint + clearer UX).
    var POPULAR_TLDS = ['.ai', '.io', '.app', '.co', '.dev', '.tech'];
    var EXTENDED_TLDS = ['.cloud', '.page', '.shop', '.studio', '.so', '.xyz'];

    var REGISTRARS = [
      { name: 'Porkbun',    prices: { '.com': 8.85, '.net': 8.85, '.ai': 65.00, '.io': 35.50, '.app': 17.00, '.co': 12.00, '.dev': 14.00, '.xyz': 1.00, '.tech': 12.00, '.cloud': 5.00, '.page': 9.00, '.shop': 4.00, '.studio': 22.00, '.so': 35.00 }, default: 12.00 },
      { name: 'Namecheap',  prices: { '.com': 8.98, '.net': 8.98, '.ai': 64.98, '.io': 34.98, '.app': 17.98, '.co': 11.98, '.dev': 13.98, '.xyz': 1.50, '.tech': 12.98, '.cloud': 6.00, '.page': 9.98, '.shop': 4.98, '.studio': 22.98, '.so': 35.98 }, default: 12.98 },
      { name: 'Cloudflare', prices: { '.com': 9.15, '.net': 9.15, '.ai': 70.00, '.io': 38.00, '.app': 18.00, '.co': 12.00, '.dev': 15.00, '.xyz': 1.00, '.tech': 13.00, '.cloud': 5.00, '.page': 9.00, '.shop': 5.00, '.studio': 24.00, '.so': 38.00 }, default: 13.00 },
      { name: 'GoDaddy',    prices: { '.com': 11.99,'.net': 11.99,'.ai': 79.99,'.io': 39.99,'.app': 19.99,'.co': 14.99,'.dev': 16.99,'.xyz': 2.99,'.tech': 14.99,'.cloud': 6.99,'.page': 11.99,'.shop': 5.99,'.studio': 24.99,'.so': 39.99 }, default: 14.99 }
    ];

    var STATUS_CLASS = {
      available:  'text-brand-accent',
      registered: 'text-rose-500',
      invalid:    'text-rose-500',
      unknown:    'text-amber-500',
      checking:   'text-muted animate-card-pulse'
    };
    var STATUS_ICON = {
      available:  '✓',
      registered: '✗',
      invalid:    '✗',
      unknown:    '?',
      checking:   '◌'
    };
    var STATUS_ARIA = {
      available:  'available',
      registered: 'taken',
      invalid:    'taken',
      unknown:    'unverified',
      checking:   'checking'
    };

    // ---------- Helpers --------------------------------------------

    function byId(id) { return document.getElementById(id); }
    function el(sel, root) { return (root || document).querySelector(sel); }
    function els(sel, root) {
      return Array.prototype.slice.call((root || document).querySelectorAll(sel));
    }
    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
      });
    }
    function rowStatusIcon(status) { return STATUS_ICON[status] || ''; }
    function rowStatusClass(status) { return STATUS_CLASS[status] || 'text-muted'; }
    function rowStatusAria(status) { return STATUS_ARIA[status] || ''; }
    function rowBadgeIcon(cand) {
      if (cand.status === 'available') return '✓';
      if (cand.status === 'registered') return '✗';
      if (cand.status === 'required-unknown') return '?';
      return '◌';
    }
    function rowBadgeAria(cand) {
      if (cand.status === 'available') return 'registerable';
      if (cand.status === 'registered') return 'unavailable';
      if (cand.status === 'required-unknown') return 'unverified';
      return 'checking';
    }
    function rowBadgeClass(cand) {
      if (cand.status === 'available') return 'text-brand-accent';
      if (cand.status === 'registered') return 'text-rose-500';
      if (cand.status === 'required-unknown') return 'text-amber-500';
      return 'text-muted animate-card-pulse';
    }
    function newId() { return 'c_' + Math.random().toString(36).slice(2, 10); }

    // ---------- Authentication (silent mock login) -----------------
    //
    // /api/v1/names/generate is behind bearerAuth. We log in once
    // per tab via the mock endpoint and cache the access token in
    // sessionStorage so the user never sees the login screen.
    var AUTH = (function () {
      var TOKEN_KEY = 'nameaha_at';
      var EMAIL_KEY = 'nameaha_email';
      function email() {
        try {
          var e = sessionStorage.getItem(EMAIL_KEY);
          if (e) return e;
        } catch (e) {}
        var fresh = 'qa-' + Date.now().toString(36) + '@example.com';
        try { sessionStorage.setItem(EMAIL_KEY, fresh); } catch (e) {}
        return fresh;
      }
      function getToken() {
        try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch (e) { return ''; }
      }
      function setToken(t) {
        try { sessionStorage.setItem(TOKEN_KEY, t); } catch (e) {}
      }
      function login() {
        return fetch('/api/v1/auth/mock/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email() })
        }).then(function (r) {
          if (!r.ok) throw new Error('login failed: ' + r.status);
          return r.json();
        }).then(function (j) {
          setToken(j.access_token);
          return j.access_token;
        });
      }
      function ensureAuth() {
        var t = getToken();
        if (t) return Promise.resolve(t);
        return login();
      }
      function authedFetch(path, opts) {
        opts = opts || {};
        opts.headers = Object.assign({}, opts.headers || {});
        return ensureAuth().then(function (tok) {
          opts.headers.Authorization = 'Bearer ' + tok;
          if (opts.body && typeof opts.body === 'object' && !opts.headers['Content-Type']) {
            opts.headers['Content-Type'] = 'application/json';
          }
          // Pass `signal` through so callers (e.g. submitDescribe) can
          // cancel the in-flight request with AbortController. Without
          // this the controller is a no-op and a stuck AI request sits
          // until the browser's default timeout.
          return fetch(path, opts).then(function (r) {
            if (r.status === 401) {
              return login().then(function (tok2) {
                opts.headers.Authorization = 'Bearer ' + tok2;
                return fetch(path, opts);
              });
            }
            return r;
          });
        });
      }
      return { authedFetch: authedFetch };
    })();

    // ---------- Session state --------------------------------------
    var state = {
      candidates: new Map(),
      latestBrief: '',
      moreCTAEl: null,
      favorites: new Set(),
      interest: {},
      briefEditing: false,
      briefBannerEl: null,
      // server-side chat session id. Created lazily on first
      // interaction so the page load doesn't pay the round-trip.
      // Lost on page refresh — by design, this is per-tab.
      sessionId: null
    };
    var inflight = false;

    // ---------- Server chat session --------------------------------
    //
    // sessionId is created lazily on the first interaction and held
    // in `state.sessionId` for the lifetime of this tab. We don't
    // persist it to sessionStorage — refresh drops the session, by
    // design. Each user-visible action (check / generate / expand /
    // favorite / select) is recorded server-side via fire-and-forget
    // POSTs so the audit trail is preserved even if the user never
    // reloads.
    function ensureSession() {
      if (state.sessionId) return Promise.resolve(state.sessionId);
      return fetch('/api/v1/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: state.activeMode || 'name' })
      }).then(function (r) {
        if (!r.ok) throw new Error('create session ' + r.status);
        return r.json();
      }).then(function (j) {
        state.sessionId = j.session_id;
        return state.sessionId;
      }).catch(function (e) {
        // Sessions are best-effort. Don't block the UI on a 5xx.
        try { console.error('session create failed', e); } catch (_) {}
        return null;
      });
    }
    function logSessionAction(action, name, tld) {
      // Fire-and-forget. We don't await; the audit row is a hint, not
      // a contract the user is waiting on.
      ensureSession().then(function (sid) {
        if (!sid) return;
        return fetch('/api/v1/sessions/' + sid + '/actions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: action, name: name || '', tld: tld || '' })
        }).catch(function () { /* swallow */ });
      });
    }
    function logSessionScope(kind, payload) {
      // Logs the request body for `check` / `generate` / `whois`
      // sessions, so the audit trail mirrors the JSON the server
      // actually processed.
      ensureSession().then(function (sid) {
        if (!sid) return;
        return fetch('/api/v1/sessions/' + sid + '/' + kind, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: typeof payload === 'string' ? payload : JSON.stringify(payload || {})
        }).catch(function () { /* swallow */ });
      });
    }

    function setSessionStatus(title, sub, phase, progress, position) {
      // position: 'top' (default) | 'bottom'. Only the bar at the
      // requested position is visible; the other is hidden. Check Now
      // has a single stage so it only uses the top bar; Describe uses
      // the top for "Inventing names…" and the bottom for the
      // downstream availability check, so the user always sees
      // progress as the name list scrolls.
      var pos = position || 'top';
      var hosts = els('.session-status');
      if (!hosts.length) return;
      hosts.forEach(function (host) {
        var match = (pos === 'top' && host.id === 'session-status-top') ||
                    (pos === 'bottom' && host.id === 'session-status-bottom');
        host.classList.toggle('hidden', !match);
      });
      var activeHost = byId(pos === 'bottom' ? 'session-status-bottom' : 'session-status-top');
      if (!activeHost) return;
      var titleEl = el('.session-status-title', activeHost);
      var subEl = el('.session-status-sub', activeHost);
      var fillEl = el('.session-status-fill', activeHost);
      var dotEl = el('.session-status-dot', activeHost);
      if (titleEl) titleEl.textContent = title || 'Working…';
      if (subEl) subEl.textContent = sub || '';
      // Error state: turn the bar red. The `phase` arg was previously
      // unused — repurpose it as the tone flag ('error' / undefined).
      var isError = phase === 'error';
      if (fillEl) {
        var pct = typeof progress === 'number' ? Math.max(8, Math.min(100, progress)) : 33;
        fillEl.style.width = pct + '%';
        fillEl.classList.toggle('animate-card-pulse', pct < 100 && !isError);
        fillEl.classList.toggle('bg-rose-500', isError);
        fillEl.classList.toggle('bg-brand-accent', !isError);
      }
      if (dotEl) {
        dotEl.classList.toggle('bg-rose-500', isError);
        dotEl.classList.toggle('bg-brand-accent', !isError);
        dotEl.classList.toggle('animate-pulse', typeof progress === 'number' ? progress < 100 : !isError);
      }
    }
    function clearSessionStatus(finalTitle, finalSub, position) {
      var pos = position || 'top';
      var host = byId(pos === 'bottom' ? 'session-status-bottom' : 'session-status-top');
      if (!host) return;
      if (finalTitle) {
        setSessionStatus(finalTitle, finalSub || '', '', 100, pos);
        setTimeout(function () {
          var titleEl = el('.session-status-title', host);
          if (titleEl && titleEl.textContent === finalTitle) {
            host.classList.add('hidden');
          }
        }, 1800);
      } else {
        host.classList.add('hidden');
      }
    }

    function bumpInterest(name, delta) {
      if (!name) return;
      state.interest[name] = (state.interest[name] || 0) + delta;
      if (state.interest[name] < 0) state.interest[name] = 0;
    }
    function interestPayload() {
      var rows = [];
      Object.keys(state.interest).forEach(function (name) {
        var score = state.interest[name] || 0;
        if (score <= 0) return;
        rows.push({ name: name, score: score, favorite: state.favorites.has(name) });
      });
      rows.sort(function (a, b) { return b.score - a.score; });
      return rows;
    }

    function updateSessionHeader() {
      var countEl = byId('session-count');
      if (!countEl) return;
      var n = state.candidates.size, reg = 0, unav = 0;
      state.candidates.forEach(function (c) {
        if (c.status === 'available') reg++;
        else if (c.status === 'registered') unav++;
      });
      countEl.textContent = n;
      var r = byId('session-registerable');
      var u = byId('session-unavailable');
      if (r) r.textContent = reg;
      if (u) u.textContent = unav;
    }
    function showSession() {
      byId('session').classList.remove('hidden');
      updateSessionHeader();
    }
    function clearSession() {
      // Clear button = full reset including UI visibility.
      resetSessionContent();
      // Hide both status bars — clearSession is a full reset.
      els('.session-status').forEach(function (h) { h.classList.add('hidden'); });
      byId('session').classList.add('hidden');
      input.value = '';
      inflight = false;
      byId('search-submit').disabled = false;
    }

    // ---------- HTTP helpers (NDJSON reader) ---------------------

    // fetchAvailabilityCheck posts {labels, tlds} and returns an
    // async iterator over verdict events. Uses fetch +
    // ReadableStream reader to parse chunked NDJSON line-by-line.
    //
    // Implementation note: we do NOT use the `signal` arg here —
    // xun's request body lifecycle treats abort signals as full
    // request cancellations, which interferes with the
    // post-response streaming read. We keep the controller as a
    // passive flag (cand.dnsAbort.aborted) and let the server-side
    // ctx cancellation naturally short-circuit the stream.
    async function* fetchAvailabilityCheck(body) {
      var r = await fetch('/api/v1/availability/check', {
        method: 'POST',
        headers: { 'Accept': 'application/x-ndjson', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!r.ok) throw new Error('/availability/check ' + r.status);
      if (!r.body) throw new Error('no body');
      var reader = r.body.getReader();
      var dec = new TextDecoder();
      var buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        var step = await reader.read();
        if (step.done) break;
        buf += dec.decode(step.value, { stream: true });
        var nl;
        while ((nl = buf.indexOf('\n')) >= 0) {
          var line = buf.slice(0, nl);
          buf = buf.slice(nl + 1);
          if (line.trim()) yield JSON.parse(line);
        }
      }
      if (buf.trim()) {
        try { yield JSON.parse(buf); } catch (e) { /* ignore trailing partial */ }
      }
    }

    function fetchNamesGenerate(body, signal) {
      // authedFetch() doesn't auto-stringify objects — fetch() turns
      // a raw object body into "[object Object]". Stringify here so
      // the handler sees the right Content-Type + JSON body.
      return AUTH.authedFetch('/api/v1/names/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: signal
      }).then(function (r) {
        if (!r.ok) throw new Error('/names/generate ' + r.status);
        return r.json();
      });
    }

    function fetchWhoisLookup(domain, signal) {
      return fetch('/api/v1/whois/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domain }),
        signal: signal
      }).then(function (r) {
        if (!r.ok) {
          // Surface the status code so renderDetailError can map it
          // to a friendly message; never leak the path in user text.
          var e = new Error('HTTP ' + r.status);
          e.status = r.status;
          throw e;
        }
        return r.json();
      });
    }

    // ---------- Per-row verdict paint ------------------------------

    function applyVerdict(cand, evt) {
      // evt: {label, tld, domain, status, source, error?}
      if (!cand || !evt) return;
      try {
        var raw = (evt.status || 'unknown').toLowerCase();
        var status = (raw === 'available' || raw === 'registered' || raw === 'invalid') ? raw : 'unknown';
        // Accept both "com" and ".com" from the server; the row's
        // data-tld uses the dotted form (".com") to mirror what the
        // user sees.
        var tldDot = '.' + String(evt.tld || '').replace(/^\./, '');
        var tldBare = tldDot.replace(/^\./, '');
        cand.domains[tldBare] = { status: status, source: evt.source || 'dns', error: evt.error };
        var btn = el('.domain-row[data-tld="' + tldDot + '"]', cand.element);
        if (btn) {
          btn.dataset.status = status;
          applyStatusIcon(btn.querySelector('.domain-row-status'), status);
        }
        recomputeRowStatus(cand);
        updateSessionHeader();
      } catch (e) {
        try { console.error('applyVerdict failed', evt, e && e.stack); } catch (_) {}
      }
    }

    function recomputeRowStatus(cand) {
      var domains = cand.domains;
      // Strip leading dot so REQUIRED_TLDS = ['.com', '.net'] and the
      // bare TLD keys we store in applyVerdict agree on lookup.
      var req = REQUIRED_TLDS.map(function (t) { return t.replace(/^\./, ''); });
      var allReqAvail = req.every(function (t) {
        var d = domains[t];
        return d && d.status === 'available';
      });
      var anyReqTaken = req.some(function (t) {
        var d = domains[t];
        return d && (d.status === 'registered' || d.status === 'invalid');
      });
      var anyReqUnknown = req.some(function (t) {
        return !domains[t] || domains[t].status === 'unknown';
      });
      var next;
      if (anyReqTaken) next = 'registered';
      else if (allReqAvail) next = 'available';
      else if (anyReqUnknown) next = 'required-unknown';
      else next = 'required-unknown';
      cand.status = next;
      // Highlight the whole row when the name is fully registerable
      // (.com + .net both available) — visual signal "this one is
      // yours to grab".
      cand.element.classList.toggle('is-registerable', next === 'available');
      // Update the right-side status icon so the verdict is visible
      // without expanding. The prototype places status + star on the
      // right; the icon morphs as required-TLD verdicts arrive.
      var rowStatusEl = el('.row-status', cand.element);
      if (rowStatusEl) {
        var iconState = next === 'available' ? 'available'
                      : next === 'registered' ? 'registered'
                      : next === 'required-unknown' ? 'unknown'
                      : 'checking';
        rowStatusEl.textContent = rowStatusIcon(iconState);
        rowStatusEl.setAttribute('aria-label', rowStatusAria(iconState));
        rowStatusEl.className = 'row-status font-mono text-sm font-bold ' + rowStatusClass(iconState);
      }
      cand.element.dataset.state = next;
    }

    function applyStatusIcon(node, status) {
      if (!node) return;
      node.textContent = rowStatusIcon(status);
      node.setAttribute('aria-label', rowStatusAria(status));
      // Update only the color/animation class — keep the base
      // font-mono / text-sm / font-bold / ml-2 classes from the
      // template so the layout doesn't reflow as verdicts arrive.
      var baseClasses = ['text-brand-accent', 'text-rose-500', 'text-amber-500', 'text-muted', 'animate-card-pulse'];
      baseClasses.forEach(function (c) { node.classList.remove(c); });
      // rowStatusClass(status) may return multiple space-separated
      // tokens (e.g. 'text-muted animate-card-pulse' for checking),
      // and DOMTokenList.add() rejects strings with whitespace.
      var classes = String(rowStatusClass(status) || '').split(/\s+/).filter(Boolean);
      classes.forEach(function (c) { node.classList.add(c); });
    }

    // ---------- Name row lifecycle ---------------------------------

    function createNameRow(name, tagline, reasoning) {
      // Guard against duplicate rows when the user mashes Enter / Check
      // Now — return the existing candidate instead of appending a
      // second row with the same name.
      var existing = findCandidate(name);
      if (existing) return existing;
      var tpl = byId('name-row-template');
      if (!tpl) return null;
      var frag = tpl.content.cloneNode(true);
      var row = frag.querySelector('.name-row');
      var id = newId();
      row.dataset.nameId = id;

      el('.name-row-name', row).textContent = name;
      el('.name-row-tagline', row).textContent = tagline || '';
      var rsnEl = el('.name-row-reasoning', row);
      if (rsnEl) rsnEl.textContent = reasoning || '';

      var reqList = el('.required-list', row);
      REQUIRED_TLDS.forEach(function (tld) {
        reqList.appendChild(buildDomainRow(name, tld, 'checking'));
      });
      // Pre-populate popular + extended rows as 'idle'. The user
      // sees popular on expand; "Show all TLDs →" reveals the rest.
      var popList = el('.popular-list', row);
      POPULAR_TLDS.forEach(function (tld) {
        popList.appendChild(buildDomainRow(name, tld, 'idle'));
      });
      var extList = el('.extended-list', row);
      EXTENDED_TLDS.forEach(function (tld) {
        extList.appendChild(buildDomainRow(name, tld, 'idle'));
      });

      var starBtn = row.querySelector('.star-btn');
      if (starBtn) {
        var isFav = state.favorites.has(name);
        starBtn.textContent = isFav ? '♥' : '♡';
        starBtn.classList.toggle('is-active', isFav);
        starBtn.setAttribute('aria-pressed', String(isFav));
        starBtn.addEventListener('click', function (ev) {
          ev.stopPropagation();
          toggleFavorite(name);
        });
      }

      // Chevron (left) and title click both expand the row. The
      // chevron rotates 180° via CSS when the row is expanded.
      var chevron = row.querySelector('.name-row-expand');
      if (chevron) {
        chevron.addEventListener('click', function (ev) {
          ev.stopPropagation();
          toggleExpand(row);
        });
      }
      var headerClick = row.querySelector('.name-row-header-click');
      if (headerClick) {
        headerClick.addEventListener('click', function () {
          toggleExpand(row);
        });
      }
      var extTrigger = row.querySelector('.extended-trigger');
      if (extTrigger) {
        extTrigger.addEventListener('click', function (ev) {
          ev.stopPropagation();
          showExtended(cand);
        });
      }

      byId('name-list').appendChild(row);
      row.classList.add('animate-card-slide-in');

      var cand = {
        id: id,
        name: name,
        tagline: tagline || '',
        element: row,
        status: 'checking-required',
        domains: {},
        expanded: false,
        activeTld: null,
        dnsAbort: null,
        dnsInflight: false,
        detailAbort: null,
        detailCache: {}
      };
      state.candidates.set(id, cand);
      showSession();
      return cand;
    }

    function buildDomainRow(label, tld, initialStatus) {
      var tpl = byId('domain-row-template');
      var frag = tpl.content.cloneNode(true);
      var li = frag.querySelector('li');
      var btn = li.querySelector('.domain-row');
      btn.dataset.tld = tld;
      btn.dataset.label = label + tld;
      btn.dataset.status = initialStatus;
      li.querySelector('.domain-row-name').textContent = label + tld;
      applyStatusIcon(li.querySelector('.domain-row-status'), initialStatus);
      btn.addEventListener('click', function () { selectDomain(btn); });
      return li;
    }

    function selectDomain(btn) {
      var row = btn.closest('.name-row');
      if (!row) return;
      var cand = state.candidates.get(row.dataset.nameId);
      if (!cand) return;
      els('.domain-row', row).forEach(function (b) { b.dataset.active = 'false'; });
      btn.dataset.active = 'true';
      var tld = btn.dataset.tld;
      cand.activeTld = tld;
      logSessionAction('select', cand.name, tld);
      renderPricePane(row, cand.name + tld, tld);
      loadWhois(cand, tld);
    }

    function renderPricePane(row, domain, tld) {
      // 1:1 with the prototype (`docs/html/app.html` _renderRegistrars).
      // Available TLD → 2-col grid (name | price).
      // Unavailable TLD → raw WHOIS dump (rendered by renderDetail).
      // The price-pane is wiped on every call; static markup from the
      // template is intentionally minimal so the JS owns the layout.
      var pane = el('.price-pane', row);
      if (!pane) return;
      pane.innerHTML = '';
      var cand = state.candidates.get(row.dataset.nameId);
      var tldBare = tld.replace(/^\./, '');
      var tldStatus = cand && cand.domains[tldBare] && cand.domains[tldBare].status;
      if (tldStatus === 'available') {
        var html = '<div class="grid grid-cols-2 gap-x-6 gap-y-4 mt-2 px-2">';
        REGISTRARS.forEach(function (reg) {
          var price = (reg.prices[tld] != null) ? reg.prices[tld] : reg.default;
          html += '<div class="flex justify-between items-center text-sm">' +
                    '<span class="text-primary font-medium">' + escapeHtml(reg.name) + '</span>' +
                    '<span class="text-muted font-mono">$' + price.toFixed(2) + '/yr</span>' +
                  '</div>';
        });
        html += '</div>';
        pane.innerHTML = html;
      } else {
        // Placeholder; renderDetail fills it with the WHOIS dump.
        var whoisBlock = document.createElement('div');
        whoisBlock.className = 'whois-block p-4 bg-[rgb(var(--surface-rgb))] rounded-xl text-xs font-mono text-muted whitespace-pre-wrap leading-relaxed overflow-x-auto';
        whoisBlock.textContent = 'whois loading...';
        pane.appendChild(whoisBlock);
      }
    }

    // ---------- Toggle / load lifecycle -----------------------------

    function toggleExpand(row) {
      var cand = state.candidates.get(row.dataset.nameId);
      if (!cand) return;
      if (cand.expanded) {
        cand.expanded = false;
        cand.element.classList.remove('is-expanded');
        if (cand.dnsAbort) cand.dnsAbort.abort();
        logSessionAction('collapse', cand.name);
        return;
      }
      cand.expanded = true;
      cand.element.classList.add('is-expanded');
      bumpInterest(cand.name, 1);
      logSessionAction('expand', cand.name);
      // Run the popular TLD batch in the background — the left
      // column paints its icons as verdicts arrive. We deliberately
      // do NOT auto-select a TLD or fire a WHOIS lookup; those are
      // user-driven (see selectDomain). The right pane stays empty
      // until the user clicks a domain row.
      var ctrl = new AbortController();
      cand.dnsAbort = ctrl;
      cand.dnsInflight = true;
      (async function () {
        try {
          for await (var evt of fetchAvailabilityCheck(
            { labels: [cand.name], tlds: POPULAR_TLDS }
          )) {
            if (ctrl.signal.aborted) break;
            if (evt.type === 'verdict') applyVerdict(cand, evt);
          }
        } catch (e) {
          if (e && e.name === 'AbortError') return;
        } finally {
          cand.dnsInflight = false;
        }
      })();
    }

    // showExtended reveals the extended TLD section and triggers the
    // batch check for those TLDs. The trigger is hidden after the
    // first click so it can't fire twice; the extended-section's
    // verdict progress fills in incrementally.
    function showExtended(cand) {
      var sect = cand.element.querySelector('.extended-section');
      var trigger = cand.element.querySelector('.extended-trigger');
      if (!sect || !trigger) return;
      if (!sect.classList.contains('hidden')) return;
      sect.classList.remove('hidden');
      trigger.classList.add('hidden');
      var ctrl = new AbortController();
      cand.dnsAbort = ctrl;
      cand.dnsInflight = true;
      (async function () {
        try {
          for await (var evt of fetchAvailabilityCheck(
            { labels: [cand.name], tlds: EXTENDED_TLDS }
          )) {
            if (ctrl.signal.aborted) break;
            if (evt.type === 'verdict') applyVerdict(cand, evt);
          }
        } catch (e) {
          if (e && e.name === 'AbortError') return;
        } finally {
          cand.dnsInflight = false;
        }
      })();
    }

    async function loadWhois(cand, tld) {
      if (cand.detailCache[tld]) {
        renderDetail(cand, tld, cand.detailCache[tld]);
        return;
      }
      if (cand.detailAbort) cand.detailAbort.abort();
      var ctrl = new AbortController();
      cand.detailAbort = ctrl;
      try {
        var detail = await fetchWhoisLookup(cand.name + tld, ctrl.signal);
        cand.detailCache[tld] = detail;
        renderDetail(cand, tld, detail);
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        renderDetailError(cand, tld, e && e.status, e && e.message);
      }
    }

    // Track all in-flight detail requests globally so a clearSession
    // (or row re-expand) can abort them without a per-row
    // AbortController flag every code path needs to remember.
    var detailAborters = new Set();

    function renderDetail(cand, tld, detail) {
      // 1:1 with the prototype's mockWhois dump. Without a `raw`
      // field on the wire shape we synthesise the same key/value
      // text from the typed RegistryDetail fields, so the visual
      // matches the prototype even when the server returns only
      // structured fields.
      var block = el('.whois-block', cand.element);
      if (!block) return;
      var lines = [];
      if (detail.registrar) lines.push('whois Registrar: ' + detail.registrar);
      if (detail.source) lines.push('whois Source: ' + detail.source);
      if (detail.created_at) lines.push('whois Creation Date: ' + detail.created_at);
      if (detail.expires_at) lines.push('whois Registry Expiry Date: ' + detail.expires_at);
      if (detail.updated_at) lines.push('whois Updated Date: ' + detail.updated_at);
      if (detail.status_codes && detail.status_codes.length) {
        lines.push('Status: ' + detail.status_codes.join(','));
      }
      if (detail.name_servers && detail.name_servers.length) {
        lines.push('Name servers: ' + detail.name_servers.join(','));
      }
      if (!lines.length) lines.push('whois lookup: no structured fields returned');
      block.textContent = lines.join('\n');
    }

    function renderDetailError(cand, tld, status, rawMsg) {
      // Map status code to a user-readable message. We never leak the
      // /whois/lookup path or a raw stack into the right pane.
      var msg;
      if (status === 502 || status === 503 || status === 504) {
        msg = 'Registry upstream is unavailable. Try again in a moment.';
      } else if (status === 429) {
        msg = 'Too many lookups. Slow down and try again shortly.';
      } else if (status === 400) {
        msg = 'Invalid domain format. Pick a different TLD.';
      } else if (status && status >= 500) {
        msg = 'Server error. Try again in a moment.';
      } else if (status && status >= 400) {
        msg = 'Lookup failed (HTTP ' + status + ').';
      } else {
        msg = 'Network error. Check your connection and try again.';
      }
      var block = el('.whois-block', cand.element);
      if (!block) return;
      block.textContent = msg;
      // Best-effort console log for debug; rawMsg has the underlying
      // error text we don't want to surface to the user.
      try { console.warn('whois lookup failed', tld, status, rawMsg); } catch (_) {}
    }

    function formatDate(s) {
      try {
        var d = new Date(s);
        if (isNaN(d.getTime())) return s;
        return d.toISOString().slice(0, 10);
      } catch (e) { return s; }
    }

    // ---------- Top-level submit handlers -------------------------

    function releaseInflight() { inflight = false; byId('search-submit').disabled = false; }

    async function submitName(value) {
      showSession();
      setSessionStatus('Checking availability…', 'Hang tight — results appear in a moment.', '', 15);
      logSessionScope('check', { labels: [value], tlds: REQUIRED_TLDS });
      var cand = createNameRow(value, '');
      if (!cand) { releaseInflight(); clearSessionStatus(); return; }
      var ctrl = new AbortController();
      cand.dnsAbort = ctrl;
      cand.dnsInflight = true;
      try {
        for await (var evt of fetchAvailabilityCheck(
          { labels: [value], tlds: REQUIRED_TLDS }
        )) {
          if (ctrl.signal.aborted) break;
          if (evt.type === 'verdict') applyVerdict(cand, evt);
          else if (evt.type === 'done') {
            clearSessionStatus(value + ' ready', 'Explore for more endings and prices.');
            // Show the "Generate New Batch" CTA so the user can pivot
            // to the describe pipeline without losing this name. Same
            // downstream flow as describe-mode's refine CTA — only
            // the entry copy differs.
            appendNewBatchCTA();
          }
        }
      } catch (e) {
        if (e && e.name !== 'AbortError') {
          showError('Could not check "' + value + '". Please try again.');
        }
      } finally {
        cand.dnsInflight = false;
        releaseInflight();
      }
    }

    async function submitDescribe(value, opts) {
      // opts.position: 'top' (default) | 'bottom'. Refine / new-batch
      // CTAs pass 'bottom' because the user is already staring at a
      // long name list — the top bar would be off-screen, the bottom
      // bar lives next to the CTA they just clicked.
      opts = opts || {};
      var pos = opts.position === 'bottom' ? 'bottom' : 'top';
      state.thinking = true;
      // showSession() reveals the parent <section id="session"> so the
      // status box it contains is actually visible. Without this the
      // setSessionStatus() call makes the inner status div visible but
      // its hidden parent keeps the user from seeing it.
      showSession();
      setSessionStatus('Inventing names…', '', null, 12, pos);
      logSessionScope('generate', {
        description: value,
        count: 8,
        industry: state.industry || '',
        tone: state.tone || '',
        interest: interestPayload()
      });
      try {
        // No client-side timeout. The server is the source of truth:
        // if it returns 200 we render the rows; if it returns 5xx we
        // surface the status code; network failures show a generic
        // message. We trust the server to bound its own latency.
        var data = await fetchNamesGenerate({
          description: value,
          count: 8,
          industry: state.industry || '',
          tone: state.tone || '',
          interest: interestPayload()
        });
        state.thinking = false;
        var names = data.names || [];
        if (!names.length) {
          clearSessionStatus('No names generated', '', pos);
          return;
        }
        // Existing rows stay in place so the user can compare the
        // new batch against what they already have.
        names.forEach(function (n) { createNameRow(n.name, n.tagline || '', n.reasoning || ''); });
        setSessionStatus('Names ready', '', null, null, pos);
        // Immediately fan out the .com/.net batch — no need to wait
        // for the user to click anything. checkAvailabilityBatch
        // already uses the bottom bar, which keeps the visible
        // status continuous when pos === 'bottom'.
        checkAvailabilityBatch(
          names.map(function (n) { return n.name; }),
          REQUIRED_TLDS
        );
      } catch (e) {
        state.thinking = false;
        var status = /\b(\d{3})\b/.exec(e.message || '');
        var code = status ? status[1] : '';
        // Log the raw error so the next failure is debuggable from
        // DevTools without guesswork. We trust the server to bound
        // its own latency; the client has no timeout.
        try { console.error('submitDescribe failed', e && e.name, e && e.message, e); } catch (_) {}
        setSessionStatus(
          'Generation failed',
          code ? 'Server returned ' + code + '. Please try again in a moment.' : 'Please try again in a moment.',
          'error', null, pos
        );
      }
    }

    async function checkAvailabilityBatch(labels, tlds) {
      var ctrl = new AbortController();
      labels.forEach(function (name) {
        var c = findCandidate(name);
        if (c) {
          if (c.dnsAbort) c.dnsAbort.abort();
          c.dnsAbort = ctrl;
          c.dnsInflight = true;
        }
      });
      setSessionStatus('Checking availability…', labels.length + ' names ready — verifying.', null, 55, 'bottom');
      try {
        for await (var evt of fetchAvailabilityCheck({ labels: labels, tlds: tlds })) {
          if (ctrl.signal.aborted) break;
          if (evt.type === 'verdict') {
            var row = findCandidate(evt.label);
            if (row) applyVerdict(row, evt);
          } else if (evt.type === 'done') {
            clearSessionStatus(labels.length + ' names ready',
                               'Explore names you like, then continue below.', 'bottom');
            appendRefineCTA();
          }
        }
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        setSessionStatus('Check failed', e.message || '', 'error', null, 'bottom');
      } finally {
        labels.forEach(function (name) {
          var c = findCandidate(name);
          if (c) c.dnsInflight = false;
        });
      }
    }

    function findCandidate(name) {
      var hit = null;
      state.candidates.forEach(function (c) {
        if (c.name === name && !hit) hit = c;
      });
      return hit;
    }

    // ---------- Refine + More CTA ----------------------------------

    // appendRefineCTA renders the "Refine + Generate again" panel below
    // the candidate list. Uses the SSR-shipped #refine-cta-template
    // (consistent styling with the rest of the surface) instead of
    // a hand-built string so design changes propagate from the
    // template without a JS edit.
    function appendRefineCTA() {
      var slot = byId('refine-slot');
      if (!slot) return;
      slot.innerHTML = '';
      var tpl = byId('refine-cta-template');
      if (!tpl) return;
      var frag = tpl.content.cloneNode(true);
      var briefEl = frag.querySelector('#refine-brief');
      if (briefEl) briefEl.value = state.latestBrief;
      slot.appendChild(frag);
      var btn = slot.querySelector('[data-action="more-names"]');
      if (btn) {
        btn.addEventListener('click', function () {
          var v = (briefEl && briefEl.value) || state.latestBrief;
          state.latestBrief = v;
          // Don't clear state.candidates — the new batch's names
          // append to the list so the user can compare.
          slot.innerHTML = '';
          // Use bottom status bar — the user is staring at a long
          // list and the top bar would be off-screen.
          submitDescribe(v, { position: 'bottom' });
        });
      }
    }

    // appendNewBatchCTA renders the "Generate New Batch" entry point
    // for users who started in 'I have a name' mode and now want AI
    // to invent fresh options from a description. Once they click,
    // they re-enter the describe pipeline (submitDescribe) — the
    // downstream flow (append → check → refine CTA) is identical to
    // describe-mode's, only the entry copy differs.
    function appendNewBatchCTA() {
      var slot = byId('refine-slot');
      if (!slot) return;
      slot.innerHTML = '';
      var tpl = byId('new-batch-cta-template');
      if (!tpl) return;
      var frag = tpl.content.cloneNode(true);
      slot.appendChild(frag);
      var briefEl = byId('new-batch-brief');
      var btn = slot.querySelector('[data-action="generate-from-description"]');
      if (btn) {
        btn.addEventListener('click', function () {
          var v = (briefEl && briefEl.value || '').trim();
          if (!v) {
            showError('Describe what you are naming first.');
            return;
          }
          state.latestBrief = v;
          slot.innerHTML = '';
          // Use bottom status bar — same reasoning as the describe
          // refine CTA: the user just clicked a CTA below the list
          // and the top bar is above the input, off-screen.
          submitDescribe(v, { position: 'bottom' });
        });
      }
    }

    // ---------- Favorites ------------------------------------------

    function toggleFavorite(name) {
      var wasFav = state.favorites.has(name);
      logSessionAction(wasFav ? 'unfavorite' : 'favorite', name);
      if (wasFav) {
        state.favorites.delete(name);
        bumpInterest(name, -10);
      } else {
        state.favorites.add(name);
        bumpInterest(name, 10);
      }
      var btns = els('.star-btn');
      btns.forEach(function (b) {
        var row = b.closest('.name-row');
        if (!row) return;
        var cand = state.candidates.get(row.dataset.nameId);
        if (!cand) return;
        var isFav = state.favorites.has(cand.name);
        b.textContent = isFav ? '♥' : '♡';
        b.classList.toggle('is-active', isFav);
        b.setAttribute('aria-pressed', String(isFav));
      });
    }

    // ---------- Toast / errors -------------------------------------

    function showError(msg) {
      var host = byId('toast-host');
      if (!host) return;
      var toast = document.createElement('div');
      toast.className = 'toast';
      toast.textContent = msg;
      host.appendChild(toast);
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 8000);
    }

    // ---------- Submit dispatcher ----------------------------------

    function submit() {
      if (inflight) return;
      var v = (input.value || '').trim();
      if (!v) return;
      inflight = true;
      byId('search-submit').disabled = true;
      // Check Now is a fresh start — wipe the previous round's rows
      // so the user sees only this query's results. Generate New Batch
      // (which calls submitDescribe directly via appendRefineCTA) does
      // NOT go through this path, so it keeps the existing list and
      // appends. Clear button also calls clearSession() directly.
      resetSessionContent();
      var mode = (state.activeMode || document.body.dataset.mode || 'name').toLowerCase();
      if (mode === 'describe') {
        state.latestBrief = v;
        submitDescribe(v).then(releaseInflight, releaseInflight);
      } else {
        submitName(v).then(releaseInflight, releaseInflight);
      }
    }

    // resetSessionContent clears the runtime state for a new query
    // without touching the on-screen status visibility (so the new
    // query's progress bar still appears in the same place). Used
    // by Check Now; clearSession() extends this with the visibility
    // reset and the Clear button.
    function resetSessionContent() {
      state.candidates.forEach(function (c) {
        if (c.dnsAbort) c.dnsAbort.abort();
        if (c.detailAbort) c.detailAbort.abort();
      });
      state.candidates.clear();
      state.moreCTAEl = null;
      state.latestBrief = '';
      state.favorites = new Set();
      state.interest = {};
      state.briefEditing = false;
      state.briefBannerEl = null;
      byId('name-list').innerHTML = '';
      var slot = byId('brief-banner-slot');
      if (slot) slot.innerHTML = '';
      var rslot = byId('refine-slot');
      if (rslot) rslot.innerHTML = '';
      // Server session: kill the old one and force a new one on the
      // first interaction. Otherwise the audit trail would tie old
      // unrelated rows to the new query.
      if (state.sessionId) {
        var sid = state.sessionId;
        state.sessionId = null;
        fetch('/api/v1/sessions/' + sid, { method: 'DELETE' }).catch(function () {});
      }
    }

    // ---------- Bootstrap ------------------------------------------

    var submitBtn = byId('search-submit');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () { submit(); });
    }
    if (input) {
      input.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' && !ev.shiftKey) {
          ev.preventDefault();
          submit();
        }
      });
    }
    var clearBtn = byId('clear-session');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () { clearSession(); });
    }

    // Auto-fire only when the SSR template set data-autostart=true (i.e.
    // the home page submit fed us ?name=...&mode=check). Without this
    // gate, every page reload would race with manual input typing and
    // either fire twice or abort the in-flight availability request.
    var tabs = byId('app-tabs');
    var autostart = tabs && tabs.dataset.autostart === 'true';
    if (autostart && input && input.value) {
      setTimeout(function () { submit(); }, 80);
    }

    // Tab switching — the "I have a name" / "I have a description"
    // buttons flip state.activeMode so submit() picks the right
    // handler. Suggestion chips (namelabs ✓ etc.) also flip + pre-fill.
    function setMode(mode, presetValue) {
      state.activeMode = mode;
      var nameBtn = el('#app-tabs [data-mode="name"]');
      var descBtn = el('#app-tabs [data-mode="describe"]');
      if (nameBtn) nameBtn.dataset.active = String(mode === 'name');
      if (descBtn) descBtn.dataset.active = String(mode === 'describe');
      if (typeof presetValue === 'string') input.value = presetValue;
    }
    function onTabClick(mode, preset) {
      return function () { setMode(mode, preset); };
    }
    var nameTabBtn = el('#app-tabs [data-mode="name"]');
    var descTabBtn = el('#app-tabs [data-mode="describe"]');
    if (nameTabBtn) nameTabBtn.addEventListener('click', onTabClick('name', ''));
    if (descTabBtn) descTabBtn.addEventListener('click', onTabClick('describe', ''));

    els('[data-demo]').forEach(function (b) {
      b.addEventListener('click', function () {
        var v = b.dataset.demo || b.textContent || '';
        var mode = v === '__ai__' ? 'describe' : 'name';
        if (v === '__ai__') v = '';
        setMode(mode, v.trim());
      });
    });

    var initialMode = tabs ? tabs.dataset.initialMode : 'name';
    setMode(initialMode || 'name');
    if (tabs && tabs.dataset.initialValue && !input.value) {
      input.value = tabs.dataset.initialValue;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupSession);
  } else {
    setupSession();
  }
})();

// ---------- Theme toggle --------------------------------------
//
// Runs on every page (nav.html is mounted by every layout). On
// load we read the persisted theme (or fall back to the OS
// preference) and immediately overwrite the template's
// `display:none` on the matching icon so the button never shows
// empty. Clicks flip theme + icon and persist the new choice.
//
// localStorage is the source of truth — prefers-color-scheme is
// only consulted on the very first visit (or when storage is
// unavailable, e.g. private browsing).
(function () {
  var KEY = 'nameaha-theme';
  var root = document.documentElement;

  function initialTheme() {
    try {
      var stored = localStorage.getItem(KEY);
      if (stored === 'dark' || stored === 'light') return stored;
    } catch (e) {}
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }

  function apply(theme) {
    var dark = theme === 'dark';
    root.classList.toggle('dark', dark);
    var sun  = document.querySelector('[data-theme-icon="sun"]');
    var moon = document.querySelector('[data-theme-icon="moon"]');
    // Overwrite the template's inline `display:none` rather than
    // toggling a class, so we don't have to teach tailwind about
    // two mutually-exclusive states per SVG.
    if (sun)  sun.style.display  = dark ? 'none'  : 'block';
    if (moon) moon.style.display = dark ? 'block' : 'none';
    try { localStorage.setItem(KEY, theme); } catch (e) {}
    return theme;
  }

  var btn = document.querySelector('[data-theme-toggle]');
  if (!btn) return; // page without a nav; nothing to wire up
  var current = apply(initialTheme());
  btn.addEventListener('click', function () {
    current = apply(current === 'dark' ? 'light' : 'dark');
  });
})();
